import { Box, CircularProgress, IconButton, Typography, Button, Tooltip } from "@mui/material";
import { useContext, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import WidgetBuilder from "./WidgetBuilder";
import { useParams } from "react-router-dom";
import {
  Responsive,
  useContainerWidth,
  verticalCompactor,
} from "react-grid-layout";
import type { Layout, LayoutItem, ResponsiveLayouts } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { AuthContext } from "../../../context/AuthContext";
import { useDashboardData } from "../../../hooks/useDashboardData";
import {
  useDashboardConfig,
  useSaveDashboardConfig,
} from "../../../hooks/useDashboardConfig";
import {
  DashboardWidget,
  WIDGET_TYPES,
  AggregateRequest,
} from "../../../types/dashboard/DashboardConfig";
import { GetDashboardDataResponse } from "../../../api/dashboard";
import { useDashboardAggregate } from "../../../hooks/useDashboardAggregate";
import { FONT_DISPLAY } from "../../theme/v2Tokens";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart, pieArcLabelClasses } from "@mui/x-charts/PieChart";
import { LineChart } from "@mui/x-charts/LineChart";
import type { ChartsAxisData } from "@mui/x-charts/ChartsOnAxisClickHandler";
import KpiStrip from "../../components/KpiStrip/KpiStrip";
import { useDashboardDrill } from "../../../hooks/useDashboardDrill";
import {
  resolveDrillDescriptor,
  canDrillCustomWidget,
} from "../../../helpers/dashboardDrill";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(customParseFormat);

/* ── Icons ────────────────────────────────────────────────────────── */

const PencilIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M11.5 2.5a1.414 1.414 0 0 1 2 2L5.5 12.5l-3 1 1-3 8-8z"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const GripIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <circle cx="5" cy="3.5" r="1" fill="currentColor" />
    <circle cx="9" cy="3.5" r="1" fill="currentColor" />
    <circle cx="5" cy="7" r="1" fill="currentColor" />
    <circle cx="9" cy="7" r="1" fill="currentColor" />
    <circle cx="5" cy="10.5" r="1" fill="currentColor" />
    <circle cx="9" cy="10.5" r="1" fill="currentColor" />
  </svg>
);

const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const EditWidgetIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
    <path
      d="M11.5 2.5a1.414 1.414 0 0 1 2 2L5.5 12.5l-3 1 1-3 8-8z"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ── Constants ────────────────────────────────────────────────────── */

const GRID_COLS = { lg: 12, md: 12, sm: 12, xs: 12, xxs: 12 };
const ROW_HEIGHT = 80;

/** Widget types that belong in the editable grid (not the fixed KPI strip). */
const CHART_WIDGET_TYPES: Set<string> = new Set([
  WIDGET_TYPES.INTERACTIONS_BY_YEAR,
  WIDGET_TYPES.MEDIA_TYPE,
  WIDGET_TYPES.CUSTOM_AGGREGATE,
]);

// V1 renders KPI tiles inside its grid at y=0..2; V2 renders them in a separate
// fixed strip and only edits chart widgets in the grid. To keep V1 happy when V2
// writes the shared config, charts must sit at y >= KPI_ROW_HEIGHT, and the
// config must contain V1's KPI tiles.
const KPI_ROW_HEIGHT = 2;

const KPI_DEFAULTS: DashboardWidget[] = [
  { id: "legal-hold", type: WIDGET_TYPES.LEGAL_HOLD, title: "Interactions in Legal Hold", layout: { x: 0, y: 0, w: 3, h: 2, minW: 2, minH: 2 } },
  { id: "purging", type: WIDGET_TYPES.PURGING, title: "Purging (30 Days)", layout: { x: 3, y: 0, w: 3, h: 2, minW: 2, minH: 2 } },
  { id: "total-interactions", type: WIDGET_TYPES.TOTAL_INTERACTIONS, title: "Total Interactions", layout: { x: 6, y: 0, w: 3, h: 2, minW: 2, minH: 2 } },
  { id: "tags", type: WIDGET_TYPES.TAGS, title: "Tags", layout: { x: 9, y: 0, w: 3, h: 2, minW: 2, minH: 2 } },
];

const DEFAULT_WIDGETS: DashboardWidget[] = [
  { id: "interactions_by_year", type: WIDGET_TYPES.INTERACTIONS_BY_YEAR, title: "Interactions by Year", layout: { x: 0, y: KPI_ROW_HEIGHT, w: 8, h: 4, minW: 4, minH: 3 } },
  { id: "media_type", type: WIDGET_TYPES.MEDIA_TYPE, title: "Interactions by Media Type", layout: { x: 8, y: KPI_ROW_HEIGHT, w: 4, h: 4, minW: 3, minH: 3 } },
];

/** Shift chart widgets down so the topmost sits below V1's KPI row, preserving relative spacing. */
function shiftChartsBelowKpiRow(charts: DashboardWidget[]): DashboardWidget[] {
  if (charts.length === 0) return charts;
  const minY = Math.min(...charts.map((w) => w.layout.y));
  if (minY >= KPI_ROW_HEIGHT) return charts;
  const offset = KPI_ROW_HEIGHT - minY;
  return charts.map((w) => ({ ...w, layout: { ...w.layout, y: w.layout.y + offset } }));
}

/* ── Grid widget card wrapper ─────────────────────────────────────── */

const BugIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 2l1.88 1.88M16 2l-1.88 1.88M9 7.13v-1a3.003 3.003 0 116 0v1" />
    <path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 014-4h4a4 4 0 014 4v3c0 3.3-2.7 6-6 6z" />
    <path d="M3 13h3M18 13h3M3 17h3M18 17h3M12 12v8" />
  </svg>
);

interface GridWidgetCardProps {
  widget: DashboardWidget;
  isEditing: boolean;
  onRemove: (id: string) => void;
  onEdit?: (widget: DashboardWidget) => void;
  debugData?: unknown;
  children: React.ReactNode;
}

function GridWidgetCard({ widget, isEditing, onRemove, onEdit, debugData, children }: GridWidgetCardProps) {
  const { t } = useTranslation(["dashboards", "common"]);
  const [copied, setCopied] = useState(false);

  const handleDebug = () => {
    const payload = {
      widget: { id: widget.id, type: widget.type, title: widget.title },
      data: debugData,
    };
    navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Box
      sx={{
        height: "100%",
        bgcolor: "var(--bg-surface)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "10px",
        padding: "16px 20px",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        position: "relative",
        transition: "box-shadow 0.15s ease",
        ...(isEditing && {
          border: "1px solid var(--border-mid)",
          "&:hover": { boxShadow: "0 0 0 1px var(--accent-dim)" },
        }),
      }}
    >
      {/* Title bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "12px",
          flexShrink: 0,
          minHeight: "20px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {isEditing && (
            <Box
              className="v2-drag-handle"
              sx={{
                cursor: "grab",
                color: "var(--text-muted)",
                display: "flex",
                alignItems: "center",
                "&:hover": { color: "var(--text-secondary)" },
                "&:active": { cursor: "grabbing" },
              }}
            >
              <GripIcon />
            </Box>
          )}
          <Box
            sx={{
              fontSize: "0.65rem",
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "var(--text-tertiary)",
            }}
          >
            {widget.title}
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
          {isEditing && onEdit && widget.type === WIDGET_TYPES.CUSTOM_AGGREGATE && (
            <Tooltip title={t("dashboards:editDashboard")} placement="top" arrow>
              <IconButton
                size="small"
                onClick={() => onEdit(widget)}
                aria-label={`Edit ${widget.title}`}
                sx={{
                  padding: "2px",
                  color: "var(--text-muted)",
                  "&:hover": {
                    color: "var(--accent)",
                    bgcolor: "var(--accent-dim)",
                  },
                }}
              >
                <EditWidgetIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={copied ? t("common:copied") : t("common:copyToClipboard")} placement="top" arrow>
            <IconButton
              size="small"
              onClick={handleDebug}
              aria-label={`Debug ${widget.title}`}
              sx={{
                padding: "2px",
                color: "var(--text-muted)",
                opacity: 0.4,
                "&:hover": {
                  opacity: 1,
                  color: "var(--text-secondary)",
                  bgcolor: "var(--bg-hover)",
                },
              }}
            >
              <BugIcon />
            </IconButton>
          </Tooltip>
          {isEditing && (
            <IconButton
              size="small"
              onClick={() => onRemove(widget.id)}
              aria-label={`Remove ${widget.title}`}
              sx={{
                padding: "2px",
                color: "var(--text-muted)",
                "&:hover": {
                  color: "var(--signal-rose)",
                  bgcolor: "rgba(251,113,133,0.12)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Widget content */}
      <Box sx={{ flex: 1, overflow: "hidden", minHeight: 0, display: "flex", flexDirection: "column" }}>
        {children}
      </Box>
    </Box>
  );
}

/* ── Responsive pie chart that measures its container ────────────── */

interface CustomAggregatePieProps {
  data: { labels: string[]; values: number[] };
  onBucketClick?: (dataIndex: number) => void;
}

function CustomAggregatePie({ data, onBucketClick }: Readonly<CustomAggregatePieProps>) {
  const pieItems = data.labels.map((label: string, i: number) => ({
    id: i,
    label,
    value: data.values[i],
    color: PIE_CHART_COLORS[i % PIE_CHART_COLORS.length],
  }));
  const total = data.values.reduce((a: number, b: number) => a + b, 0);

  return (
    <Box sx={{ display: "flex", flex: 1, minHeight: 0, width: "100%", overflow: "hidden", gap: 1 }}>
      {/* Chart — no right margin, fills available space */}
      <Box sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
        <PieChart
          series={[{
            data: pieItems,
            innerRadius: "50%",
            paddingAngle: 1,
            cornerRadius: 3,
            arcLabelMinAngle: 30,
            arcLabel: (item) => total > 0 ? `${Math.round((item.value / total) * 100)}%` : "",
            highlightScope: { fade: "global", highlight: "item" },
            faded: { innerRadius: 30, additionalRadius: -10, color: "gray" },
          }]}
          colors={PIE_CHART_COLORS}
          margin={{ top: 10, bottom: 10, left: 10, right: 10 }}
          sx={{
            ...(onBucketClick && {
              "& .MuiPieArc-root": { cursor: "pointer" },
              "& .MuiChartsLegend-series": { cursor: "pointer" },
            }),
            [`& .${pieArcLabelClasses.root}`]: { fill: "#fff", fontSize: 12, fontWeight: 600 },
          }}
          onItemClick={onBucketClick ? (_e, pieItemIdentifier) => onBucketClick(pieItemIdentifier.dataIndex) : undefined}
          slotProps={{
            legend: {
              hidden: true,
              onItemClick: onBucketClick ? (_e, _ctx, index) => onBucketClick(index) : undefined,
            },
          }}
        />
      </Box>
      {/* Legend — separate element, fixed width. Each row drills the matching slice when clickable. */}
      <Box sx={{ width: 150, flexShrink: 0, display: "flex", flexDirection: "column", justifyContent: "center", gap: "3px", overflow: "hidden" }}>
        {pieItems.map((item) => (
          <Box
            key={item.id}
            onClick={onBucketClick ? () => onBucketClick(item.id) : undefined}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              minHeight: 0,
              padding: "2px 4px",
              borderRadius: "4px",
              ...(onBucketClick && {
                cursor: "pointer",
                transition: "background-color 0.15s ease",
                "&:hover": { bgcolor: "var(--bg-hover)" },
              }),
            }}
          >
            <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: item.color, flexShrink: 0 }} />
            <Box sx={{ fontSize: "0.68rem", color: "var(--text-secondary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {item.label}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/* ── Custom Aggregate Widget (fetches its own data) ──────────────── */

function CustomAggregateContent({ widget, isEditing }: { widget: DashboardWidget; isEditing: boolean }) {
  const { t } = useTranslation(["common"]);
  const { customerId } = useParams<{ customerId: string }>();
  const { drill, canDrill } = useDashboardDrill("v2", isEditing);
  const config = widget.config;

  const request: AggregateRequest | null = config
    ? {
        aggregationType: config.aggregationType ?? "terms",
        field: config.aggregationType === "count" ? undefined : config.field,
        interval: config.interval ?? undefined,
        size: config.size ?? undefined,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        from: config.filters?.from ?? undefined,
        to: config.filters?.to ?? undefined,
        agentName: config.filters?.agentName ?? undefined,
        direction: config.filters?.direction ?? undefined,
        interactionType: config.filters?.interactionType ?? undefined,
        skill: config.filters?.skill ?? undefined,
        group: config.filters?.group ?? undefined,
        tags: config.filters?.tags ?? undefined,
      }
    : null;

  const { data, isLoading } = useDashboardAggregate(customerId, request, widget.id);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, gap: 1 }}>
        <CircularProgress size={16} />
        <Box component="span" sx={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>{t("common:loading")}</Box>
      </Box>
    );
  }

  if (!data || (data.labels.length === 0 && data.total === 0)) {
    return <Box sx={{ color: "var(--text-muted)", fontSize: "0.82rem" }}>{t("common:noDataAvailable")}</Box>;
  }

  const chartType = config?.chartType ?? "bar";
  const widgetFilters = config?.filters ?? {};
  const isDrillable = canDrill && !isLoading && !!data && canDrillCustomWidget(widget);

  const handleBucketClick = isDrillable
    ? (dataIndex: number) => {
        const desc = resolveDrillDescriptor(widget, dataIndex, data.labels);
        if (!desc) return;
        drill(desc, widgetFilters);
      }
    : undefined;

  const handleAxisClick = handleBucketClick
    ? (_e: MouseEvent, params: ChartsAxisData | null) => {
        if (params) handleBucketClick(params.dataIndex);
      }
    : undefined;

  // KPI
  if (chartType === "kpi") {
    const onTileClick = isDrillable
      ? (e: React.MouseEvent) =>
          drill({ kind: "none" }, widgetFilters, {
            metaKey: e.metaKey,
            ctrlKey: e.ctrlKey,
            button: e.button,
          })
      : undefined;
    return (
      <Box
        onClick={onTileClick}
        onAuxClick={onTileClick}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          borderRadius: "8px",
          cursor: isDrillable ? "pointer" : "default",
          ...(isDrillable && {
            transition: "background-color 0.15s ease",
            "&:hover": { bgcolor: "var(--bg-hover)" },
          }),
        }}
      >
        <Box
          sx={{
            fontFamily: FONT_DISPLAY,
            fontSize: "2.5rem",
            fontWeight: 500,
            color: "var(--text-primary)",
          }}
        >
          {data.total.toLocaleString()}
        </Box>
      </Box>
    );
  }

  // Pie
  if (chartType === "pie") {
    return <CustomAggregatePie data={data} onBucketClick={handleBucketClick} />;
  }

  // Line
  if (chartType === "line") {
    return (
      <Box sx={{ width: "100%", height: "100%", flex: 1, minHeight: 0 }}>
      <LineChart
        xAxis={[{ scaleType: "band", data: data.labels }]}
        series={[{ data: data.values, color: BAR_CHART_COLORS[0] }]}
        margin={{ left: 60, right: 16, top: 16, bottom: 32 }}
        onAxisClick={handleAxisClick}
        sx={{
          ...(isDrillable && { cursor: "pointer" }),
          "& .MuiChartsAxis-tickLabel": { fill: "var(--text-secondary) !important", fontSize: "0.75rem !important" },
          "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": { stroke: "var(--border-mid) !important" },
          "& .MuiChartsGrid-line": { stroke: "var(--border-subtle) !important" },
        }}
        grid={{ horizontal: true }}
      />
      </Box>
    );
  }

  // Bar (default)
  return (
    <Box sx={{ width: "100%", height: "100%", flex: 1, minHeight: 0 }}>
    <BarChart
      xAxis={[{ scaleType: "band", data: data.labels }]}
      series={[{ data: data.values, color: BAR_CHART_COLORS[0] }]}
      margin={{ left: 60, right: 16, top: 16, bottom: 32 }}
      borderRadius={4}
      onAxisClick={handleAxisClick}
      sx={{
        ...(isDrillable && { cursor: "pointer" }),
        "& .MuiChartsAxis-tickLabel": { fill: "var(--text-secondary) !important", fontSize: "0.75rem !important" },
        "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": { stroke: "var(--border-mid) !important" },
        "& .MuiChartsGrid-line": { stroke: "var(--border-subtle) !important" },
      }}
      grid={{ horizontal: true }}
    />
    </Box>
  );
}

/* ── Widget content renderer ──────────────────────────────────────── */

interface WidgetContentProps {
  widget: DashboardWidget;
  dashboardData: GetDashboardDataResponse;
  isEditing: boolean;
}

const BAR_CHART_COLORS = ["#4A7A9B"];
const PIE_CHART_COLORS = ["#4A7A9B", "#7B6B94", "#B89B5E", "#A66B73", "#5A8A72"];

function WidgetContent({ widget, dashboardData, isEditing }: WidgetContentProps) {
  const { t } = useTranslation(["common"]);
  const { drill, canDrill } = useDashboardDrill("v2", isEditing);

  switch (widget.type) {
    case WIDGET_TYPES.INTERACTIONS_BY_YEAR: {
      const iby = dashboardData.interactionsByYear;
      if (!iby) {
        return <Box sx={{ color: "var(--text-muted)", fontSize: "0.82rem" }}>{t("common:noDataAvailable")}</Box>;
      }
      const years = Object.keys(iby as unknown as Record<string, number>);
      const counts = Object.values(iby as unknown as Record<string, number>) as number[];
      if (years.length === 0) {
        return <Box sx={{ color: "var(--text-muted)", fontSize: "0.82rem" }}>{t("common:noDataAvailable")}</Box>;
      }
      const yearLabels = years.map((day) => dayjs.utc(day).format("YYYY"));
      return (
        <BarChart
          xAxis={[{ scaleType: "band", data: yearLabels }]}
          series={[{ data: counts, color: BAR_CHART_COLORS[0] }]}
          margin={{ left: 60, right: 16, top: 16, bottom: 32 }}
          borderRadius={4}
          onAxisClick={canDrill ? (_e: MouseEvent, params: ChartsAxisData | null) => {
            if (!params) return;
            const yearStr = yearLabels[params.dataIndex];
            const start = dayjs.utc(yearStr, "YYYY", true);
            if (!start.isValid()) return;
            const end = start.add(1, "year");
            drill(
              { kind: "dateRange", from: start.format("YYYY-MM-DDTHH:mm:ss"), to: end.format("YYYY-MM-DDTHH:mm:ss") },
              {},
            );
          } : undefined}
          sx={{
            ...(canDrill && { cursor: "pointer" }),
            "& .MuiChartsAxis-tickLabel": {
              fill: "var(--text-secondary) !important",
              fontSize: "0.75rem !important",
            },
            "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": {
              stroke: "var(--border-mid) !important",
            },
            "& .MuiChartsGrid-line": {
              stroke: "var(--border-subtle) !important",
            },
          }}
          grid={{ horizontal: true }}
        />
      );
    }

    case WIDGET_TYPES.MEDIA_TYPE: {
      const mtData = dashboardData.interactionByType;
      if (!mtData || mtData.length === 0) {
        return <Box sx={{ color: "var(--text-muted)", fontSize: "0.82rem" }}>{t("common:noDataAvailable")}</Box>;
      }
      const total = mtData.reduce((acc, item) => acc + item.value, 0);
      const pieItems = mtData.map((item, i) => ({
        id: i,
        label: item.label,
        value: item.value,
        color: PIE_CHART_COLORS[i % PIE_CHART_COLORS.length],
      }));
      return (
        <Box sx={{ display: "flex", justifyContent: "center", flex: 1, minHeight: 0, width: "100%", height: "100%" }}>
          <PieChart
            series={[
              {
                data: pieItems,
                innerRadius: "50%",
                paddingAngle: 1,
                cornerRadius: 3,
                arcLabelMinAngle: 30,
                arcLabel: (item) => `${Math.round((item.value / total) * 100)}%`,
                highlightScope: { fade: "global", highlight: "item" },
                faded: { innerRadius: 30, additionalRadius: -10, color: "gray" },
              },
            ]}
            colors={PIE_CHART_COLORS}
            margin={{ top: 10, bottom: 10, left: 10, right: 180 }}
            onItemClick={canDrill ? (_e, pieItemIdentifier) => {
              const label = mtData[pieItemIdentifier.dataIndex]?.label;
              if (!label) return;
              drill({ kind: "field", field: "interactionType", value: label }, {});
            } : undefined}
            sx={{
              ...(canDrill && {
                "& .MuiPieArc-root": { cursor: "pointer" },
                "& .MuiChartsLegend-series": { cursor: "pointer" },
              }),
              [`& .${pieArcLabelClasses.root}`]: {
                fill: "#fff",
                fontSize: 12,
                fontWeight: 600,
              },
            }}
            slotProps={{
              legend: {
                position: { vertical: "middle", horizontal: "right" },
                direction: "column",
                labelStyle: {
                  fill: "var(--text-secondary)",
                  fontSize: 11,
                },
                onItemClick: canDrill ? (_e, _ctx, index) => {
                  const label = mtData[index]?.label;
                  if (!label) return;
                  drill({ kind: "field", field: "interactionType", value: label }, {});
                } : undefined,
              },
            }}
          />
        </Box>
      );
    }

    case WIDGET_TYPES.CUSTOM_AGGREGATE:
      return <CustomAggregateContent widget={widget} isEditing={isEditing} />;

    default:
      return (
        <Box sx={{ color: "var(--text-muted)", fontSize: "0.82rem" }}>
          Unknown widget: {widget.type}
        </Box>
      );
  }
}

/* ── Styles for react-grid-layout inside V2 theme ─────────────────── */

const gridOverrideStyles = {
  "& .react-grid-item.react-grid-placeholder": {
    bgcolor: "var(--accent-dim)",
    borderRadius: "10px",
    border: "2px dashed var(--accent)",
    opacity: 0.4,
  },
  "& .react-grid-item > .react-resizable-handle": {
    // subtle resize handle in bottom-right
    "&::after": {
      borderColor: "var(--text-muted) !important",
      width: "6px !important",
      height: "6px !important",
    },
  },
} as const;

/* ── Helpers ──────────────────────────────────────────────────────── */

function findOpenPosition(widgets: DashboardWidget[], w: number, h: number, cols: number = 12): { x: number; y: number } {
  if (widgets.length === 0) return { x: 0, y: 0 };

  // Find the bottom of all widgets
  let maxBottom = 0;
  for (const widget of widgets) {
    const bottom = widget.layout.y + widget.layout.h;
    if (bottom > maxBottom) maxBottom = bottom;
  }

  // Scan row by row, column by column for the first open slot
  for (let y = 0; y <= maxBottom; y++) {
    for (let x = 0; x <= cols - w; x++) {
      // Check if this position overlaps any existing widget
      const overlaps = widgets.some((widget) => {
        const wl = widget.layout;
        return x < wl.x + wl.w && x + w > wl.x && y < wl.y + wl.h && y + h > wl.y;
      });
      if (!overlaps) return { x, y };
    }
  }

  // No open slot found, place at bottom-left
  return { x: 0, y: maxBottom };
}

/* ── Main component ───────────────────────────────────────────────── */

export default function V2Dashboard() {
  const { t } = useTranslation(["dashboards", "common"]);
  const { user } = useContext(AuthContext);
  const { customerId } = useParams<{ customerId: string }>();
  const { data: dashboardData, isLoading: isDataLoading } = useDashboardData(customerId);
  const { data: configData, isLoading: isConfigLoading } = useDashboardConfig(customerId);
  const saveMutation = useSaveDashboardConfig(customerId);
  const canEdit = user?.hasFeatureFlag("EditableDashboards") ?? false;

  const { width, containerRef, mounted } = useContainerWidth();

  // On hard refresh, the container width starts small then expands as flex
  // layout settles. We bucket the width to avoid re-rendering on every pixel,
  // but use it as a key to force react-grid-layout to re-compute when the
  // container size changes significantly.

  const [isEditing, setIsEditing] = useState(false);
  const [localWidgets, setLocalWidgets] = useState<DashboardWidget[] | null>(null);
  // Tracks whether the user clicked "Reset to defaults" within the current edit
  // session. When true, the next Save writes V1's KPI defaults too — required
  // when the existing config is missing KPI tiles, so reset can recover them.
  const [resetPending, setResetPending] = useState(false);
  const [widgetBuilderOpen, setWidgetBuilderOpen] = useState(false);
  const [editingBuilderWidget, setEditingBuilderWidget] = useState<DashboardWidget | undefined>(undefined);

  // Active widgets: local state while editing, otherwise server config (filtered to chart types) or defaults
  const widgets = useMemo(() => {
    if (localWidgets) return localWidgets;
    const fromConfig = configData?.widgets?.filter((w) => CHART_WIDGET_TYPES.has(w.type));
    return fromConfig && fromConfig.length > 0 ? fromConfig : DEFAULT_WIDGETS;
  }, [localWidgets, configData]);

  // Convert widgets to react-grid-layout format
  // Define layouts for ALL breakpoints with the same 12-col layout
  // to prevent squishing when Responsive picks a different breakpoint
  const layouts: ResponsiveLayouts = useMemo(() => {
    const items: LayoutItem[] = widgets.map((w) => ({
      i: w.id,
      x: w.layout.x,
      y: w.layout.y,
      w: w.layout.w,
      h: w.layout.h,
      minW: w.layout.minW ?? 2,
      minH: w.layout.minH ?? 2,
    }));
    return { lg: items, md: items, sm: items, xs: items, xxs: items };
  }, [widgets]);

  const handleLayoutChange = useCallback(
    (layout: Layout) => {
      if (!isEditing) return;
      setLocalWidgets((prev) => {
        const fromConfig = configData?.widgets?.filter((w) => CHART_WIDGET_TYPES.has(w.type));
        const current = prev ?? (fromConfig && fromConfig.length > 0 ? fromConfig : DEFAULT_WIDGETS);
        const layoutMap = new Map(layout.map((l) => [l.i, l]));
        return current.map((widget) => {
          const layoutItem = layoutMap.get(widget.id);
          if (!layoutItem) return widget;
          const { x, y, w, h } = widget.layout;
          if (x === layoutItem.x && y === layoutItem.y && w === layoutItem.w && h === layoutItem.h) {
            return widget;
          }
          return {
            ...widget,
            layout: {
              ...widget.layout,
              x: layoutItem.x,
              y: layoutItem.y,
              w: layoutItem.w,
              h: layoutItem.h,
            },
          };
        });
      });
    },
    [isEditing, configData]
  );

  const handleEdit = () => {
    const fromConfig = configData?.widgets?.filter((w) => CHART_WIDGET_TYPES.has(w.type));
    setLocalWidgets(fromConfig && fromConfig.length > 0 ? fromConfig : [...DEFAULT_WIDGETS]);
    setIsEditing(true);
  };

  const handleSave = () => {
    if (localWidgets) {
      // V2 only edits chart widgets; preserve any non-chart widgets (e.g. V1 KPI tiles)
      // from the existing config so saving here doesn't drop them. After a reset,
      // restore V1's KPI defaults instead so the config is V1-compatible even when
      // the existing one was missing them. Also keep chart widgets below V1's KPI
      // row — V2's compactor pulls them to y=0 visually, which would otherwise
      // collide with the KPI tiles when V1 renders.
      const preserved = configData?.widgets?.filter((w) => !CHART_WIDGET_TYPES.has(w.type)) ?? [];
      const nonCharts = resetPending ? KPI_DEFAULTS : preserved;
      const charts = shiftChartsBelowKpiRow(localWidgets);
      saveMutation.mutate([...nonCharts, ...charts]);
    }
    setIsEditing(false);
    setLocalWidgets(null);
    setResetPending(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setLocalWidgets(null);
    setResetPending(false);
  };

  const handleRemoveWidget = (widgetId: string) => {
    setLocalWidgets((prev) => {
      const fromConfig = configData?.widgets?.filter((w) => CHART_WIDGET_TYPES.has(w.type));
      const current = prev ?? (fromConfig && fromConfig.length > 0 ? fromConfig : DEFAULT_WIDGETS);
      return current.filter((w) => w.id !== widgetId);
    });
  };

  const handleResetDefaults = () => {
    // Stage the chart defaults locally and flag a reset; the user previews the
    // result and confirms via Save (matches V1's behavior). The reset flag
    // tells handleSave to write V1's KPI defaults too.
    setLocalWidgets([...DEFAULT_WIDGETS]);
    setResetPending(true);
  };

  const handleOpenAddWidget = () => {
    setEditingBuilderWidget(undefined);
    setWidgetBuilderOpen(true);
  };

  const handleOpenEditWidget = (widget: DashboardWidget) => {
    setEditingBuilderWidget(widget);
    setWidgetBuilderOpen(true);
  };

  const handleCloseWidgetBuilder = () => {
    setWidgetBuilderOpen(false);
    setEditingBuilderWidget(undefined);
  };

  const handleSaveWidget = (widget: DashboardWidget) => {
    setLocalWidgets((prev) => {
      const fromConfig = configData?.widgets?.filter((w) => CHART_WIDGET_TYPES.has(w.type));
      const current = prev ?? (fromConfig && fromConfig.length > 0 ? fromConfig : [...DEFAULT_WIDGETS]);
      const existingIndex = current.findIndex((w) => w.id === widget.id);
      if (existingIndex >= 0) {
        // Replace existing widget
        const updated = [...current];
        updated[existingIndex] = widget;
        return updated;
      }
      // Append new widget with positioned layout
      const pos = findOpenPosition(current, widget.layout.w, widget.layout.h);
      const positioned = {
        ...widget,
        layout: { ...widget.layout, x: pos.x, y: pos.y },
      };
      return [...current, positioned];
    });
  };

  const handleDeleteWidget = (widgetId: string) => {
    handleRemoveWidget(widgetId);
  };

  const isLoading = isDataLoading || isConfigLoading;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 24px",
          flexShrink: 0,
        }}
      >
        <Typography
          variant="h2"
          sx={{
            fontFamily: FONT_DISPLAY,
            fontWeight: 500,
            fontSize: "1.4rem",
            letterSpacing: "-0.02em",
          }}
        >
          {t("dashboards:dashboard")}
        </Typography>

        {/* Edit mode buttons */}
        {canEdit && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {isEditing ? (
              <>
                <Button
                  size="small"
                  onClick={handleResetDefaults}
                  sx={{
                    fontSize: "0.75rem",
                    textTransform: "none",
                    color: "var(--text-secondary)",
                    "&:hover": { bgcolor: "var(--bg-hover)" },
                  }}
                >
                  {t("dashboards:resetToDefaults")}
                </Button>
                <Button
                  size="small"
                  onClick={handleOpenAddWidget}
                  startIcon={<PlusIcon />}
                  sx={{
                    fontSize: "0.75rem",
                    textTransform: "none",
                    color: "var(--accent)",
                    border: "1px solid var(--accent-dim)",
                    "&:hover": { bgcolor: "var(--accent-dim)" },
                  }}
                >
                  {t("dashboards:addWidget")}
                </Button>
                <Button
                  size="small"
                  onClick={handleCancel}
                  sx={{
                    fontSize: "0.75rem",
                    textTransform: "none",
                    color: "var(--text-secondary)",
                    border: "1px solid var(--border-mid)",
                    "&:hover": { bgcolor: "var(--bg-hover)" },
                  }}
                >
                  {t("common:cancel")}
                </Button>
                <Button
                  size="small"
                  onClick={handleSave}
                  disabled={saveMutation.isPending}
                  sx={{
                    fontSize: "0.75rem",
                    textTransform: "none",
                    color: "var(--bg-deep)",
                    bgcolor: "var(--accent)",
                    "&:hover": { bgcolor: "var(--accent)", opacity: 0.9 },
                    "&.Mui-disabled": { opacity: 0.5 },
                  }}
                >
                  {saveMutation.isPending ? t("common:saving") : t("common:save")}
                </Button>
              </>
            ) : (
              <Tooltip title={t("dashboards:customizeDashboard")} arrow>
                <IconButton
                  size="small"
                  onClick={handleEdit}
                  aria-label={t("dashboards:editDashboard")}
                  sx={{
                    color: "var(--text-tertiary)",
                    "&:hover": { bgcolor: "var(--bg-hover)", color: "var(--text-secondary)" },
                  }}
                >
                  <PencilIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>

      {/* Loading state */}
      {isLoading && (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={40} sx={{ color: "var(--accent)" }} />
        </Box>
      )}

      {/* KPI strip — fixed, always visible, not editable */}
      {!isLoading && dashboardData && (
        <KpiStrip data={dashboardData} />
      )}

      {/* Grid container — always in DOM so useContainerWidth can measure */}
      <Box
        ref={containerRef as React.RefObject<HTMLDivElement>}
        sx={{
          flex: 1,
          overflowY: "auto",
          padding: "0 24px 24px",
          display: isLoading || !dashboardData ? "none" : "block",
          ...gridOverrideStyles,
        }}
      >
          {mounted && width > 100 && !isLoading && dashboardData && (
            <Responsive
              className="v2-dashboard-grid"
              width={width}
              layouts={layouts}
              breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
              cols={GRID_COLS}
              rowHeight={ROW_HEIGHT}
              dragConfig={{
                enabled: isEditing,
                bounded: false,
                handle: ".v2-drag-handle",
                threshold: 3,
              }}
              resizeConfig={{
                enabled: isEditing,
                handles: ["se"],
              }}
              onLayoutChange={handleLayoutChange}
              compactor={verticalCompactor}
              margin={[16, 16]}
              containerPadding={[0, 0]}
            >
              {widgets.map((widget) => {
                const debugData =
                  widget.type === WIDGET_TYPES.INTERACTIONS_BY_YEAR
                    ? { endpoint: `GET /v1/customers/${customerId}/dashboard`, field: "interactionsByYear", value: dashboardData.interactionsByYear }
                    : widget.type === WIDGET_TYPES.MEDIA_TYPE
                      ? { endpoint: `GET /v1/customers/${customerId}/dashboard`, field: "interactionByType", value: dashboardData.interactionByType }
                      : { endpoint: `GET /v1/customers/${customerId}/dashboard`, config: widget.config };
                return (
                <div key={widget.id}>
                  <GridWidgetCard
                    widget={widget}
                    isEditing={isEditing}
                    onRemove={handleRemoveWidget}
                    onEdit={handleOpenEditWidget}
                    debugData={debugData}
                  >
                    <WidgetContent
                      widget={widget}
                      dashboardData={dashboardData}
                      isEditing={isEditing}
                    />
                  </GridWidgetCard>
                </div>
                );
              })}
            </Responsive>
          )}
      </Box>

      {/* Empty state */}
      {!isLoading && !dashboardData && (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--text-muted)",
            fontSize: "0.9rem",
          }}
        >
          {t("dashboards:noDashboardData")}
        </Box>
      )}

      {/* Widget Builder drawer */}
      <WidgetBuilder
        open={widgetBuilderOpen}
        onClose={handleCloseWidgetBuilder}
        onSave={handleSaveWidget}
        onDelete={handleDeleteWidget}
        editingWidget={editingBuilderWidget}
        customerId={customerId}
      />
    </Box>
  );
}
